import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import {
  spacing,
  mediaMax,
  fontSize,
  fontWeight,
  mediaMin,
} from 'presentations/views/styles';
import ResourcePageTemplate from 'presentations/views/components/templates/ResourcePageTemplate';
import ResourceList, {
  Props as ResourceListProps,
} from 'presentations/views/components/organisms/ResourceList';
import ResourceListSidebar from 'presentations/views/components/organisms/ResourceListSidebar';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import Header from 'presentations/views/components/organisms/Header';
import { useResourceListPage } from './hooks';
import Breadcrumbs from '../../organisms/Breadcrumbs';
import { primary } from 'presentations/views/styles/color';
import { ResourceTagItem } from 'presentations/api/schema/resources';
import {
  showAdSquare,
  subImageAdId,
} from 'presentations/views/components/molecules/OwnedContentBanner';
import TopTitle from './TopTitle';

export interface Props {
  title: string;
  isTop?: boolean;
  description?: string;
  tag: ResourceTagItem | null;
  category: string | null;
  resourceListProps: ResourceListProps;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useResourceListPage({
    tag: props.tag,
    category: props.category,
    resources: props.resourceListProps.items,
  });

  return (
    <ResourcePageTemplate
      breadcrumbs={<Breadcrumbs routes={state.routes} />}
      header={<Header viewer={state.viewer} />}
    >
      {props.category && props.category !== 'blog' ? (
        <CategoryNameContainer>
          <CategoryIcon name="note-text-outline" />
          <CategoryTitle>{state.categoryName}</CategoryTitle>
        </CategoryNameContainer>
      ) : props.tag ? (
        <CategoryNameContainer>
          <CategoryIcon name="tag-outline" />
          <CategoryTitle>{props.tag.name}</CategoryTitle>
        </CategoryNameContainer>
      ) : (
        props.isTop && (
          <TopTitle
            title={
              <BlogTitleImage
                src="logo/logo_magazine.svg"
                alt={props.title}
                width={295}
                height={49}
              />
            }
            description={t('description')}
          />
        )
      )}
      <Container>
        <ResourceListContainer>
          <ResourceList {...props.resourceListProps} />
        </ResourceListContainer>
        <SidebarContainer>
          <AdContainer>{showAdSquare({ adId: subImageAdId })}</AdContainer>
          <ResourceListSidebar />
          <StickyAdContainer>
            {showAdSquare({ adId: subImageAdId })}
          </StickyAdContainer>
        </SidebarContainer>
      </Container>
    </ResourcePageTemplate>
  );
}

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto ${spacing(80)};
  display: flex;
  flex-direction: row;

  ${mediaMax.desktop} {
    padding: 0 ${spacing(24)};
  }
  ${mediaMax.tablet} {
    flex-direction: column;
    padding: 0 ${spacing(12)};
  }
  ${mediaMax.xs} {
    width: 100%;
  }
`;

const ResourceListContainer = styled.div`
  padding: 0 ${spacing(48)} ${spacing(72)} 0;

  ${mediaMax.tablet} {
    padding: 0;
  }
`;

const SidebarContainer = styled.div`
  width: 300px;
  min-width: 300px;

  ${mediaMax.tablet} {
    margin: ${spacing(32)} 0 0 0;
    width: inherit;
    min-width: inherit;
  }
`;

const CategoryIcon = styled(Icon)`
  color: ${primary};
  margin-right: ${spacing(8)};
  width: 32px !important;
  height: 32px !important;
  ${mediaMax.tablet} {
    width: 24px !important;
    height: 24px !important;
  }
`;
const CategoryTitle = styled.div`
  font-size: ${fontSize(32)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;
const CategoryNameContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 1080px;
  margin: ${spacing(40)} auto;
  ${mediaMax.tablet} {
    margin: ${spacing(24)} auto;
    padding: 0 ${spacing(12)};
  }
`;

const BlogTitleImage = styled(Image)`
  width: 295px;
  height: auto;
  ${mediaMax.tablet} {
    width: 223px;
  }
`;
const AdContainer = styled.div`
  margin-bottom: ${spacing(24)};
`;
const StickyAdContainer = styled.div`
  display: none;
  ${mediaMin.desktop} {
    display: block;
    position: sticky;
    top: 96px;
  }
`;
